import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';
import { useMemo } from 'react';

// Query Keys for Admin Terms of Service
export const adminTermsOfServiceKeys = {
  all: ['adminTermsOfService'],
  lists: () => [...adminTermsOfServiceKeys.all, 'list'],
  list: (filters) => [...adminTermsOfServiceKeys.lists(), { filters }],
  details: () => [...adminTermsOfServiceKeys.all, 'detail'],
  detail: (id) => [...adminTermsOfServiceKeys.details(), id],
  settings: () => [...adminTermsOfServiceKeys.all, 'settings'],
};

/**
 * Custom hook to fetch a list of terms of service sections for admin.
 * @param {object} params - Query parameters for filtering and pagination.
 * @param {object} options - React Query options.
 */
export const useAdminTermsOfService = (params = {}, options = {}) => {
  const queryClient = useQueryClient();

  const cleanFilters = useMemo(() => {
    const newFilters = { ...params };
    // Remove undefined or null values
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === undefined || newFilters[key] === null || newFilters[key] === '') {
        delete newFilters[key];
      }
    });
    return newFilters;
  }, [params]);

  const cleanFiltersWithPagination = useMemo(() => {
    return {
      ...cleanFilters,
      per_page: cleanFilters.per_page || 10,
    };
  }, [cleanFilters]);

  const { data: sectionsResponse, isLoading, error } = useQuery({
    queryKey: adminTermsOfServiceKeys.list(cleanFiltersWithPagination),
    queryFn: async () => {
      const response = await api.get('/admin/terms-of-service', { params: cleanFiltersWithPagination });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options,
  });

  const sections = useMemo(() => sectionsResponse?.data?.data || [], [sectionsResponse]);
  const pagination = useMemo(() => sectionsResponse?.data?.meta || {}, [sectionsResponse]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/terms-of-service', data);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminTermsOfServiceKeys.lists());
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/terms-of-service/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminTermsOfServiceKeys.lists());
      queryClient.invalidateQueries(adminTermsOfServiceKeys.detail(variables.id));
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/terms-of-service/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminTermsOfServiceKeys.lists());
      queryClient.invalidateQueries(adminTermsOfServiceKeys.detail(variables));
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/terms-of-service/${id}`);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminTermsOfServiceKeys.lists());
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const { data: settings, isLoading: isLoadingSettings } = useQuery({
    queryKey: adminTermsOfServiceKeys.settings(),
    queryFn: async () => {
      const response = await api.get('/admin/terms-of-service/settings');
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put('/admin/terms-of-service/settings', data);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminTermsOfServiceKeys.settings());
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const activeStatusOptions = useMemo(() => [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ], []);

  return {
    sections,
    isLoading,
    error,
    filters: cleanFilters,
    pagination,
    activeStatusOptions,
    statistics: {},
    isLoadingStatistics: false,
    statisticsError: null,
    createSection: createMutation.mutate,
    isCreating: createMutation.isLoading,
    updateSection: updateMutation.mutate,
    isUpdating: updateMutation.isLoading,
    toggleStatus: toggleStatusMutation.mutate,
    isToggling: toggleStatusMutation.isLoading,
    deleteSection: deleteMutation.mutate,
    isDeleting: deleteMutation.isLoading,
    settings: settings || {},
    isLoadingSettings,
    updateSettings: updateSettingsMutation.mutate,
    isUpdatingSettings: updateSettingsMutation.isLoading,
  };
};

